import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';

const emptyForm = {
  category: 'appareil',
  sourceText: '',
  targetText: '',
};

const categoryLabels = {
  appareil: 'Appareil',
  pathology: 'Pathologie',
  flag: 'Flag',
};

const categoryColors = {
  appareil: 'primary',
  pathology: 'secondary',
  flag: 'warning',
};

function AideTranslations() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null); // null = all categories
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchTranslations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/aide-translations', {
        headers: {
          'x-access-token': token,
          Authorization: `Bearer ${token}`,
        },
      });
      setTranslations(response.data || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Impossible de charger les traductions.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  const toggleCategoryFilter = (category) => {
    setActiveCategory((prev) => (prev === category ? null : category));
  };

  const filteredTranslations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return translations.filter((translation) => {
      const matchesCategory = !activeCategory || translation.category === activeCategory;
      if (!matchesCategory) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return [translation.category, translation.sourceText, translation.targetText]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [translations, query, activeCategory]);

  const openCreateDialog = () => {
    setEditingTranslation(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (translation) => {
    setEditingTranslation(translation);
    setForm({
      category: translation.category || 'appareil',
      sourceText: translation.sourceText || '',
      targetText: translation.targetText || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTranslation(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        category: form.category,
        sourceText: form.sourceText.trim(),
        targetText: form.targetText.trim(),
      };

      if (editingTranslation) {
        await axios.put(`/aide-translations/${editingTranslation._id}`, payload, {
          headers: {
            'x-access-token': token,
            Authorization: `Bearer ${token}`,
          },
        });
        setSnackbar({ open: true, message: 'Traduction mise à jour.', severity: 'success' });
      } else {
        await axios.post('/aide-translations', payload, {
          headers: {
            'x-access-token': token,
            Authorization: `Bearer ${token}`,
          },
        });
        setSnackbar({ open: true, message: 'Traduction créée.', severity: 'success' });
      }

      closeDialog();
      fetchTranslations();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Impossible d’enregistrer la traduction.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (translation) => {
    const confirmed = window.confirm(`Supprimer la traduction "${translation.sourceText}" ?`);
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/aide-translations/${translation._id}`, {
        headers: {
          'x-access-token': token,
          Authorization: `Bearer ${token}`,
        },
      });
      setSnackbar({ open: true, message: 'Traduction supprimée.', severity: 'success' });
      fetchTranslations();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Impossible de supprimer la traduction.',
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ maxWidth: 1400 }}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TranslateIcon sx={{ color: '#22356D' }} />
            <Box>
              <Typography variant="h4" fontWeight={800} color="#1f2a44">
                Traductions aide au comptoir
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gérez ici les équivalents français vers arabe utilisés par l’application.
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Nouvelle traduction
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.entries(categoryLabels).map(([category, label]) => {
          const isActive = activeCategory === category;
          return (
            <Chip
              key={category}
              label={label}
              color={categoryColors[category]}
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => toggleCategoryFilter(category)}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 700 : 400,
                boxShadow: isActive ? '0 2px 8px rgba(15, 23, 42, 0.15)' : 'none',
              }}
            />
          );
        })}
      </Box>

      <TextField
        fullWidth
        label="Rechercher par catégorie, français ou arabe"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mb: 3, backgroundColor: 'white' }}
      />

      <Box sx={{ backgroundColor: 'white', borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Catégorie</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Texte français</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Texte arabe</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>Chargement...</TableCell>
              </TableRow>
            ) : filteredTranslations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>Aucune traduction trouvée.</TableCell>
              </TableRow>
            ) : (
              filteredTranslations.map((translation) => (
                <TableRow key={translation._id} hover>
                  <TableCell>
                    <Chip label={categoryLabels[translation.category] || translation.category} size="small" color={categoryColors[translation.category] || 'default'} />
                  </TableCell>
                  <TableCell>{translation.sourceText}</TableCell>
                  <TableCell sx={{ direction: 'rtl', textAlign: 'right' }}>{translation.targetText}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton color="primary" onClick={() => openEditDialog(translation)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(translation)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingTranslation ? 'Modifier la traduction' : 'Nouvelle traduction'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="translation-category-label">Catégorie</InputLabel>
              <Select
                labelId="translation-category-label"
                label="Catégorie"
                value={form.category}
                onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              >
                <MenuItem value="appareil">Appareil</MenuItem>
                <MenuItem value="pathology">Pathologie</MenuItem>
                <MenuItem value="flag">Flag</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Texte français"
              value={form.sourceText}
              onChange={(event) => setForm((prev) => ({ ...prev, sourceText: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Texte arabe"
              value={form.targetText}
              onChange={(event) => setForm((prev) => ({ ...prev, targetText: event.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{ direction: 'rtl' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDialog} color="inherit">
            Annuler
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AideTranslations;